import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * A lightweight "Added to cart" toast.
 *
 * Listens for the theme's cart:update (CartAddEvent), shows the single item that
 * was just added, and fades in. It auto-dismisses after a few seconds, or the
 * shopper can close it early via the close button.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} item - Container the added-item markup is rendered into.
 * @property {HTMLTemplateElement} [moneyFormat] - Holds the shop money format template.
 *
 * @extends {Component<Refs>}
 */
class CartToastComponent extends Component {
  requiredRefs = ['item'];

  /** How long the toast stays visible before auto-dismissing (ms). */
  #dismissDelay = 5000;

  /** @type {number | undefined} */
  #dismissTimer;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#clearDismissTimer();
  }

  /**
   * @param {CustomEvent} event
   */
  #onCartUpdate = (event) => {
    const detail = event.detail ?? {};
    const data = detail.data ?? {};

    // Only respond to genuine adds (product form / quick add), not cart edits or errors.
    if (data.source !== 'product-form-component' || data.didError) return;

    // Don't stack on top of the cart drawer when it auto-opens or is already open.
    if (document.querySelector('cart-drawer-component[auto-open]')) return;
    if (document.querySelector('.cart-drawer__dialog[open]')) return;

    const item = this.#resolveAddedItem(detail);
    if (!item) return;

    this.#renderItem(item);
    this.show();
  };

  /**
   * Picks the line item that was just added out of the fresh cart.
   * @param {{ resource?: { items?: any[] }, sourceId?: string }} detail
   * @returns {any | null}
   */
  #resolveAddedItem(detail) {
    const items = detail.resource?.items;
    if (!Array.isArray(items) || items.length === 0) return null;

    const variantId = detail.sourceId != null ? String(detail.sourceId) : null;
    if (variantId) {
      const match = items.find((entry) => String(entry.variant_id ?? entry.id) === variantId);
      if (match) return match;
    }

    // Fallback: the most recently added line.
    return items[items.length - 1];
  }

  /**
   * @param {any} item - A Shopify cart line item (from /cart.js).
   */
  #renderItem(item) {
    const imageUrl = item.featured_image?.url || item.image;
    const alt = item.featured_image?.alt || item.product_title || item.title || '';
    const imageMarkup = imageUrl
      ? `<img class="cart-toast__image" src="${this.#sizedImage(imageUrl, 240)}" alt="${escapeHtml(
          alt
        )}" width="96" height="96" loading="eager" decoding="async">`
      : '<span class="cart-toast__image cart-toast__image--placeholder" aria-hidden="true"></span>';

    const variantMarkup =
      item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-toast__variant">${escapeHtml(item.variant_title)}</p>`
        : '';

    const qty = Number(item.quantity) || 1;
    const price = this.#formatMoney(item.final_line_price);
    const priceMarkup = price ? `<span class="cart-toast__price">${price}</span>` : '';

    this.refs.item.innerHTML = `
      ${imageMarkup}
      <div class="cart-toast__details">
        <p class="cart-toast__title">${escapeHtml(item.product_title || item.title || '')}</p>
        ${variantMarkup}
        <p class="cart-toast__meta"><span class="cart-toast__qty">${qty} &times;</span>${priceMarkup}</p>
      </div>
    `;
  }

  /**
   * @param {number | null | undefined} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    if (cents == null) return '';

    const template = this.refs.moneyFormat;
    const format =
      (template instanceof HTMLTemplateElement ? template.content.textContent : null)?.trim() || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    try {
      return formatMoney(Number(cents), format, currency);
    } catch (_) {
      return '';
    }
  }

  /**
   * Requests a resized variant of a Shopify CDN image URL.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #sizedImage(url, width) {
    try {
      const resolved = new URL(url, window.location.origin);
      resolved.searchParams.set('width', String(width));
      return resolved.href;
    } catch (_) {
      return url;
    }
  }

  show() {
    this.hidden = false;
    // Force a reflow so the fade-in transition runs from the hidden state.
    void this.offsetWidth;
    this.classList.add('cart-toast--visible');

    // Restart the auto-dismiss countdown on every show (e.g. rapid re-adds).
    this.#clearDismissTimer();
    this.#dismissTimer = setTimeout(this.close, this.#dismissDelay);
  }

  #clearDismissTimer() {
    if (this.#dismissTimer) {
      clearTimeout(this.#dismissTimer);
      this.#dismissTimer = undefined;
    }
  }

  /** Fades out and hides the toast. Wired to the close button via `on:click`. */
  close = () => {
    this.#clearDismissTimer();
    this.classList.remove('cart-toast--visible');

    /** @param {TransitionEvent} transitionEvent */
    const onEnd = (transitionEvent) => {
      if (transitionEvent.target !== this || transitionEvent.propertyName !== 'opacity') return;
      this.removeEventListener('transitionend', onEnd);
      if (!this.classList.contains('cart-toast--visible')) this.hidden = true;
    };
    this.addEventListener('transitionend', onEnd);

    // Fallback in case transitionend doesn't fire (e.g. reduced motion).
    setTimeout(() => {
      if (!this.classList.contains('cart-toast--visible')) this.hidden = true;
    }, 400);
  };
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

if (!customElements.get('cart-toast-component')) {
  customElements.define('cart-toast-component', CartToastComponent);
}
